import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    Box,
    Title,
    Text,
    Paper,
    TextInput,
    Select,
    Alert,
    Badge,
    SimpleGrid,
    Button,
    Group,
    Anchor,
} from '@mantine/core';
import { IconSearch, IconExternalLink } from '@tabler/icons-react';
import { SchoolMap } from './SchoolMap';
import { LoadingState } from '../common/LoadingState';
import { FullSchool } from '../../types/Localities';
import { scimSchoolService } from '../../services/ScimLocalityService';
import { getReferenceDisplay } from '../../types/ScimReference';
import { getAvailableTileStyles } from '../../utils/mapTileUtils';
import { useMantineColorScheme } from '@mantine/core';
import { useTheme as useThemeContext } from '../../contexts/ThemeContext';
import { debugLog } from '../../utils/debugLog';

interface SchoolMapViewProps {
    authResolved?: boolean;
}

export const SchoolMapView: React.FC<SchoolMapViewProps> = ({ authResolved = false }) => {
    const { accounts } = useMsal();
    const isAuthenticated = accounts.length > 0;
    const themeContext = useThemeContext();
    const { colorScheme } = useMantineColorScheme();
    const isDarkMode = themeContext?.isDarkMode || colorScheme === 'dark';
    const [schools, setSchools] = useState<FullSchool[]>([]);
    const [filteredSchools, setFilteredSchools] = useState<FullSchool[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSchool, setSelectedSchool] = useState<FullSchool | null>(null);
    const [mapTileStyle, setMapTileStyle] = useState<'voyager' | 'darkMatter' | 'tonerLite' | 'positron' | 'openstreetmap'>(() => {
        const saved = localStorage.getItem('mapTileStyle');
        if (saved && ['voyager', 'darkMatter', 'tonerLite', 'positron', 'openstreetmap'].includes(saved)) {
            return saved as 'voyager' | 'darkMatter' | 'tonerLite' | 'positron' | 'openstreetmap';
        }
        return 'voyager';
    });
    const availableTileStyles = getAvailableTileStyles(isDarkMode);

    useEffect(() => {
        localStorage.setItem('mapTileStyle', mapTileStyle);
    }, [mapTileStyle]);

    useEffect(() => {
        if (isDarkMode && mapTileStyle === 'tonerLite') {
            setMapTileStyle('voyager');
        }
        if (!isDarkMode && mapTileStyle === 'darkMatter') {
            setMapTileStyle('voyager');
        }
    }, [isDarkMode, mapTileStyle]);

    useEffect(() => {
        if (!authResolved) {
            debugLog(`⏳ SchoolMapView: Waiting for authentication to be resolved...`);
            return;
        }
        loadSchools();
    }, [authResolved]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredSchools(schools);
        } else {
            const filtered = schools.filter(school =>
                school.DisplayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                school.Address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                school.City?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                school.State?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredSchools(filtered);
        }
    }, [searchTerm, schools]);

    const loadSchools = async () => {
        try {
            setLoading(true);
            setError(null);

            debugLog('🗺️ SchoolMapView: Loading full schools for map...');

            const result = await scimSchoolService.getFullSchoolsForMap({ page: 1, pageSize: 999 });
            setSchools(result.localities);
            setFilteredSchools(result.localities);

        } catch (err) {
            console.error('❌ Error loading schools for map:', err);
            setError(err instanceof Error ? err.message : 'Failed to load schools');
        } finally {
            setLoading(false);
        }
    };

    const handleSchoolSelect = (school: FullSchool) => {
        setSelectedSchool(school);
    };

    const getSchoolDetailUrl = (school: FullSchool): string => {
        return `#localities/school/${school.Id}`;
    };

    const navigateToSchool = (school: FullSchool) => {
        const url = getSchoolDetailUrl(school);
        // NOTE: We intentionally keep this as a basic hash assignment.
        // The map view doesn't "stay on" this route; it just hands off navigation to the main app.
        window.location.hash = url;
        // No need to manually dispatch hashchange; setting location.hash already triggers it.
    };

    const schoolsWithAddress = filteredSchools.filter(school =>
        school.Address && school.City && school.State
    );

    if (loading) {
        return (
            <LoadingState variant="centered" minHeight={400} />
        );
    }

    return (
        <Box>
            <Group justify="space-between" align="center" mb="md">
                <Title order={1}>
                    🗺️ School Map
                </Title>
                <Badge variant="outline" color="blue">
                    {schoolsWithAddress.length} schools with addresses
                </Badge>
            </Group>

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {!isAuthenticated && (
                <Alert color="blue" mb="md">
                    Using mock data for demonstration
                </Alert>
            )}

            {/* Search and Filters */}
            <Paper p="md" mb="md">
                <SimpleGrid cols={{ base: 1, md: 12 }}>
                    <Box style={{ gridColumn: 'span 5' }}>
                        <TextInput
                            placeholder="Search schools by name, address, or city..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            leftSection={<IconSearch size={18} />}
                            size="sm"
                        />
                    </Box>
                    <Box style={{ gridColumn: 'span 3' }}>
                        <Select
                            label="Map Theme"
                            value={mapTileStyle}
                            onChange={(v) => v && setMapTileStyle(v as typeof mapTileStyle)}
                            data={availableTileStyles.map((style) => ({ value: style.value, label: style.label }))}
                            size="sm"
                        />
                    </Box>
                    <Group gap="sm" wrap="wrap" align="center" style={{ gridColumn: 'span 4' }}>
                        <Badge variant="outline" size="sm">
                            Total: {schools.length}
                        </Badge>
                        <Badge variant="outline" size="sm" color="blue">
                            With Address: {schoolsWithAddress.length}
                        </Badge>
                        {selectedSchool && (
                            <Badge variant="outline" size="sm" color="gray">
                                Selected: {selectedSchool.DisplayName}
                            </Badge>
                        )}
                    </Group>
                </SimpleGrid>
            </Paper>

            {/* Map */}
            <Paper style={{ overflow: 'hidden' }}>
                <SchoolMap
                    schools={schoolsWithAddress}
                    selectedSchool={selectedSchool}
                    onSchoolSelect={handleSchoolSelect}
                    height="600px"
                    showPopup={true}
                    tileStyle={mapTileStyle}
                />
            </Paper>

            {/* Selected School Info */}
            {selectedSchool && (
                <Paper p="md" mt="md" withBorder>
                    <Group justify="space-between" align="center" mb="md">
                        <Title order={5}>
                            📍 Selected School: {selectedSchool.DisplayName}
                        </Title>
                        <Button
                            size="xs"
                            ml="md"
                            leftSection={<IconExternalLink size={16} />}
                            onClick={() => navigateToSchool(selectedSchool)}
                        >
                            View Details
                        </Button>
                    </Group>

                    <SimpleGrid cols={{ base: 1, md: 2 }}>
                        <Box>
                            <Text size="sm" c="dimmed">
                                Address
                            </Text>
                            <Text size="sm">
                                {selectedSchool.Address}
                            </Text>
                            <Text size="sm">
                                {selectedSchool.City}, {selectedSchool.State} {selectedSchool.Zip}
                            </Text>
                        </Box>
                        <Box>
                            <Text size="sm" c="dimmed">
                                Contact
                            </Text>
                            <Text size="sm">
                                📞 {selectedSchool.PhoneNumber || 'No phone number'}
                            </Text>
                            {selectedSchool.Principals && selectedSchool.Principals.length > 0 && (
                                <Text size="sm">
                                    👥 Principals: {selectedSchool.Principals.map(principal =>
                                        getReferenceDisplay(principal)
                                    ).join(', ')}
                                </Text>
                            )}
                        </Box>
                    </SimpleGrid>

                    <Box mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                        <Text size="sm" c="dimmed" mb="xs">
                            Quick Navigation:
                        </Text>
                        <Anchor
                            href={getSchoolDetailUrl(selectedSchool)}
                            onClick={(e) => {
                                e.preventDefault();
                                navigateToSchool(selectedSchool);
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                textDecoration: 'none'
                            }}
                        >
                            <IconExternalLink size={14} />
                            Open {selectedSchool.DisplayName} in School Details
                        </Anchor>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};
